import { findIndex } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import envConfig from 'inventory-listing/config/index.js';
import { DEFAULT_PAGE_SIZE } from 'inventory-listing/constants/listing.js';
import { COLANDER_GROUPS } from 'inventory-listing/constants/colanders.js';

import { ContentZonesService, LocaleService, UrlService, WindowProxy } from '@sm360/widget-commons-core-services';
import { AnalyticsService, TRACKING_TAGS as TAGS, UtilsService } from '@sm360/widget-commons-general';
import { Vehicle, ShowroomService, Quote } from '@sm360/widget-commons-vehicle';
import { TILE_MODES } from '@sm360/widget-commons-tiles';
import ThemedTile from '@sm360/widget-themed-tile';

import 'inventory-listing/components/ResultVehicles/ResultVehicles.scss';

const imageServerDomain = envConfig.source.image;

class ResultVehicles extends Component {
    constructor(props) {
        super(props);
        this.handleFavoriteClick = this.handleFavoriteClick.bind(this);
        this.redirect = this.redirect.bind(this);

        this.alreadyScrolledToVehicle = false;
        this.cardItemsRef = React.createRef();
    }

    componentWillReceiveProps(newProps) {
        if (!newProps.vehicles) {
            return;
        }

        if (!this.alreadyScrolledToVehicle) {
            this.scrollToSection();
        }
    }

    componentDidUpdate(nextProps) {
        if ((nextProps.carfaxKey !== this.props.carfaxKey) !== '') {
            const windowProxy = WindowProxy.get();
            if (
                windowProxy &&
                this.props.carfaxKey &&
                !windowProxy.CarProofBadge &&
                !windowProxy.document.getElementById('carproof-badge-js')
            ) {
                const script = document.createElement('script');
                script.defer = true;
                script.src = '//badging.carproof.com/Scripts/CarProofBadges.min.js';
                /*
                Is is important that the script tag below has the `carproof-badge-js` id
                even with the carproof buyout at the moment. Otherwise, the badges
                will not be displayed. You can verify the id to use
                here:  https://badgingapi.carfax.ca/Scripts/CarProofBadges.js
                */
                script.id = 'carproof-badge-js';
                script.setAttribute('data-thirdpartykey', this.props.carfaxKey);
                script.setAttribute('data-language', LocaleService.getSimpleLocale());
                windowProxy.document.head.appendChild(script);
            } else if (windowProxy.CarProofBadge) {
                windowProxy.CarProofBadge.refresh();
            }
        }
        if (nextProps.vehicles?.length > 0) {
            const vehiclesParent = this.cardItemsRef?.current;
            if (vehiclesParent) {
                UtilsService.alignCardItems(vehiclesParent.querySelectorAll('.listing-tile-link'), [
                    '.listing-new-tile-drivePowerTrains',
                ]);
            }
        }
    }

    scrollToSection() {
        if (!WindowProxy.get()) {
            return;
        }

        const pageNumber = UrlService.getUrlParameter('page');
        if (!pageNumber) {
            return;
        }

        const getSelectedVehicle = parseInt(pageNumber, 10) * DEFAULT_PAGE_SIZE - DEFAULT_PAGE_SIZE;
        const tileElement = WindowProxy.get().document.getElementsByClassName('listing-tile-link');
        const element = tileElement && pageNumber >= 2 && tileElement[getSelectedVehicle];
        if (element) {
            element.scrollIntoView({
                behavior: 'instant',
                block: 'start',
            });
            this.alreadyScrolledToVehicle = true;
        }
    }

    handleFavoriteClick(id) {
        const vehicleState = this.state.vehicles;
        const index = findIndex(vehicleState, { id });
        vehicleState[index].favorite = !vehicleState[index].favorite;
    }

    redirect(event, vehicle) {
        event.preventDefault();
        event.stopPropagation();
        const config = {};

        const vehicleModel =
            vehicle instanceof Vehicle.VehicleModelV25 ? vehicle : new Vehicle.VehicleModelV25(vehicle);

        const quote = new Quote.QuoteModelV25().set('vehicle', vehicleModel);

        UrlService.addOrReplaceParameter('page', this.props.pageNumber);
        // For Groupe site use the primary orgranization id and location
        if (vehicle?.primaryOrganizationUnit?.id) {
            config.orgUnitId = vehicle.primaryOrganizationUnit.id;
            // Location is not available for now, will be available with microservice change
            config.location = vehicle?.primaryOrganizationUnit?.location;
        }
        // Creates public QuoteV25
        ShowroomService.saveQuoteV25(quote.getShowroomQuote(), true, config).then((id) => {
            const newQuote = Quote.QuoteService.resetQuoteFromShowroomSaveV25(quote, id);
            const url = newQuote.getVDPUrl(false, false, config.orgUnitId);
            const windowProxy = WindowProxy.get();
            if (windowProxy && url) {
                windowProxy.location.href = url;
            }
        });
    }

    handleTileClicked(event, vehicle) {
        const gaTag = TAGS.MOST_POPULAR_VEHICLES(WindowProxy.location().pathname, vehicle.model.name);
        AnalyticsService.gaTrigger(gaTag);
        this.redirect(event, vehicle);
    }

    render() {
        const { vehicleLoading, priceLoading, showCarfax } = this.props;

        const tradedText = ContentZonesService.getContentZone(
            'widget.listing.used',
            'widget.listing.used.as.traded.text'
        );
        const certifiedText = ContentZonesService.getContentZone(
            'widget.listing.used',
            'widget.listing.used.certified.texte'
        );
        const warrantyText = ContentZonesService.getContentZone(
            'widget.listing.used',
            'widget.listing.used.extended.warranty.text'
        );
        const inspectedText = ContentZonesService.getContentZone(
            'widget.listing.used',
            'widget.listing.used.inspected.text'
        );
        const defaultPicture = ContentZonesService.getContentZone(
            'widget.listing.used',
            'widget.listing.used.default.picture'
        );
        const tradedBadge = ContentZonesService.getContentZone(
            'widget.listing.used',
            'widget.listing.used.as.traded.img'
        );
        const certifiedBadge = ContentZonesService.getContentZone(
            'widget.listing.used',
            'widget.listing.used.certified.img'
        );
        const warrantyBadge = ContentZonesService.getContentZone(
            'widget.listing.used',
            'widget.listing.used.extended.warranty.img'
        );
        const inspectedBadge = ContentZonesService.getContentZone(
            'widget.listing.used',
            'widget.listing.used.inspected.img'
        );

        const defaultDefaultPictureSrc =
            imageServerDomain && `${imageServerDomain}/ir/w400c/images/widget/showroom/asset/non-photo.png`;

        // Return new tile if colanderGroupSlug is new or find new word from colanderCategorySlug string
        // Otherwise Return used tile
        const getTileMode = () => {
            if (
                this.props.colanderGroupSlug === COLANDER_GROUPS.NEW ||
                (this.props.colanderCategorySlug &&
                    (this.props.colanderCategorySlug?.toLowerCase().includes('new') ||
                        this.props.colanderCategorySlug?.toLowerCase().includes('neufs')))
            ) {
                return TILE_MODES.INVENTORY_NEW;
            }
            return TILE_MODES.INVENTORY_USED;
        };

        const { vehicles } = this.props;

        const vehicleTiles =
            vehicles?.length > 0 ? (
                <ThemedTile theme="tile-a">
                    {(Tile) =>
                        vehicles.map((vehicle) => {
                            const vehicleTileBlock = (
                                <Tile
                                    mode={getTileMode()}
                                    defaultPicture={
                                        defaultPicture ? defaultPicture.getValue() : defaultDefaultPictureSrc
                                    }
                                    certifiedText={certifiedText ? certifiedText.getValue() : ''}
                                    inspectedText={inspectedText ? inspectedText.getValue() : ''}
                                    tradedText={tradedText ? tradedText.getValue() : ''}
                                    warrantyText={warrantyText ? warrantyText.getValue() : ''}
                                    certifiedBadge={certifiedBadge ? certifiedBadge.getValue() : ''}
                                    inspectedBadge={inspectedBadge ? inspectedBadge.getValue() : ''}
                                    tradedBadge={tradedBadge ? tradedBadge.getValue() : ''}
                                    warrantyBadge={warrantyBadge ? warrantyBadge.getValue() : ''}
                                    onClick={(event) => this.handleTileClicked(event, vehicle)}
                                    vehicle={vehicle}
                                    hasCarfaxBadge={
                                        this.props.colanderGroupSlug !== COLANDER_GROUPS.NEW &&
                                        this.props.carfaxKey !== ''
                                    }
                                    isLoading={vehicleLoading}
                                    priceLoading={priceLoading}
                                    showCarfax={showCarfax}
                                    displayCreditRate={this.props.displayCreditRate}
                                    motoInsightNewEnabled={this.props.motoInsightNewEnabled}
                                    motoInsightUsedEnabled={this.props.motoInsightUsedEnabled}
                                    roadsterNewEnabled={this.props.roadsterNewEnabled}
                                    roadsterUsedEnabled={this.props.roadsterUsedEnabled}
                                    eshopNewEnabled={this.props.eshopNewEnabled}
                                    eshopUsedEnabled={this.props.eshopUsedEnabled}
                                    shopClickDriveNewEnabled={this.props.shopClickDriveNewEnabled}
                                    shopClickDriveUsedEnabled={this.props.shopClickDriveUsedEnabled}
                                    roadsterLink={this.props.roadsterLink}
                                    dealerCode={this.props.dealerCode}
                                    dealerBAC={this.props.dealerBAC}
                                />
                            );

                            return (
                                <div className="listing-tile-link" key={vehicle.vehicleId}>
                                    {vehicleTileBlock}
                                </div>
                            );
                        })
                    }
                </ThemedTile>
            ) : (
                // Mimick 6 shallow vehicles for loading purposes
                <ThemedTile theme="tile-a">
                    {(Tile) =>
                        Array(6)
                            .fill(0)
                            .map((_, index) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <div className="listing-tile-link" key={index}>
                                    <Tile mode={getTileMode()} isLoading />
                                </div>
                            ))
                    }
                </ThemedTile>
            );

        return (
            <div className="listing-tiles" id="listing-wrapper">
                <div className="listing-tiles-wrapper" ref={this.cardItemsRef}>
                    {vehicleTiles}
                </div>
            </div>
        );
    }
}
ResultVehicles.defaultProps = {
    vehicleLoading: false,
    priceLoading: false,
    displayCreditRate: false,
    colanderCategorySlug: '',
    motoInsightNewEnabled: false,
    motoInsightUsedEnabled: false,
    roadsterNewEnabled: false,
    roadsterUsedEnabled: false,
    eshopNewEnabled: false,
    eshopUsedEnabled: false,
    shopClickDriveNewEnabled: false,
    shopClickDriveUsedEnabled: false,
    roadsterLink: '',
    dealerCode: '',
    dealerBAC: '',
};

ResultVehicles.propTypes = {
    carfaxKey: PropTypes.string.isRequired,
    colanderGroupSlug: PropTypes.string.isRequired,
    vehicles: PropTypes.arrayOf(PropTypes.instanceOf(Vehicle.VehicleModelV25)).isRequired,
    pageNumber: PropTypes.number.isRequired,
    vehicleLoading: PropTypes.bool,
    priceLoading: PropTypes.bool,
    showCarfax: PropTypes.bool.isRequired,
    displayCreditRate: PropTypes.bool,
    colanderCategorySlug: PropTypes.string,
    motoInsightNewEnabled: PropTypes.bool,
    motoInsightUsedEnabled: PropTypes.bool,
    roadsterNewEnabled: PropTypes.bool,
    roadsterUsedEnabled: PropTypes.bool,
    eshopNewEnabled: PropTypes.bool,
    eshopUsedEnabled: PropTypes.bool,
    shopClickDriveNewEnabled: PropTypes.bool,
    shopClickDriveUsedEnabled: PropTypes.bool,
    roadsterLink: PropTypes.string,
    dealerCode: PropTypes.string,
    dealerBAC: PropTypes.string,
};

ResultVehicles.displayName = 'ResultVehicles';

export { ResultVehicles };

export default ResultVehicles;
